import React, { useState } from 'react';
import { Card, Col, Container, Form, Row, Button } from 'react-bootstrap';
import NavigationHeader from './components/NavigationHeader';
import FooterWithPolicies from './components/FooterWithPolicies';
import { FaBuilding, FaRobot, FaGraduationCap, FaDollarSign, FaPhone, FaCalendar, FaVideo } from 'react-icons/fa';
import axios from 'helpers/api';
import { toast } from 'react-toastify';

const JoinBrokerage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentBrokerage: '',
    yearsExperience: '',
    annualTransactions: '',
    message: '',
    bestTimeToCall: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Send to backend API
      await axios.post('/public-leads/join-brokerage', formData);
      
      toast.success('Thank you for your interest! We\'ll contact you within 24 hours.');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        currentBrokerage: '',
        yearsExperience: '',
        annualTransactions: '',
        message: '',
        bestTimeToCall: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <FaRobot className="fs-1 text-primary" />,
      title: 'AI-Powered Technology',
      description: 'Advanced lead management system that works 24/7'
    },
    {
      icon: <FaBuilding className="fs-1 text-primary" />,
      title: 'Premium Office Space',
      description: 'Professional Chandler location with meeting rooms - no fees'
    },
    {
      icon: <FaGraduationCap className="fs-1 text-primary" />,
      title: 'Continuous Education',
      description: 'Free CE courses and AI marketing training included'
    },
    {
      icon: <FaDollarSign className="fs-1 text-primary" />,
      title: 'Competitive Compensation',
      description: 'Industry-leading opportunities for experienced agents'
    }
  ];

  return (
    <>
      <NavigationHeader />
      
      {/* Hero Section */}
      <section className="py-5 bg-gradient" style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col lg={12} className="text-center">
              <h1 className="display-4 fw-bold mb-4">The Future of Real Estate is Here</h1>
              <p className="lead mb-4">Join Arizona's First AI-Powered Brokerage</p>
              <p className="fs-5 mb-5">
                Where technology handles the busy work, so you can focus on what matters:<br />
                building relationships and closing deals.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button 
                  variant="light" 
                  size="lg"
                  className="px-4"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <FaVideo className="me-2" />
                  Watch 2-Min Demo
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg"
                  className="px-4"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Schedule Consultation
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Trust Bar */}
      <section className="py-3 bg-white border-bottom">
        <Container>
          <div className="text-center text-muted">
            Licensed in Arizona #CO721005000 | Equal Housing Opportunity | Realtor® | 47 Agents Strong
          </div>
        </Container>
      </section>

      {/* Introduction */}
      <section className="py-5">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h2 className="h1 mb-4">A Different Kind of Brokerage</h2>
              <p className="lead">
                At KairosIQ AI Realty, we believe agents should be the face of real estate, 
                while the brokerage runs the engine. Our proprietary AI technology handles 
                lead response, nurturing, and qualification 24/7 - even while you sleep.
              </p>
            </Col>
          </Row>
          
          <Row className="mt-5">
            <Col md={4} className="text-center mb-4">
              <div className="mb-3">⚡</div>
              <h4>Instant Response</h4>
              <p>Leads answered in under 5 minutes</p>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div className="mb-3">🎯</div>
              <h4>Smart Qualification</h4>
              <p>AI analyzes and scores every lead automatically</p>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div className="mb-3">📈</div>
              <h4>Higher Conversion</h4>
              <p>Convert 35% more leads with perfect follow-up</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Philosophy Section */}
      <section className="py-5 bg-light">
        <Container>
          <h2 className="text-center mb-5">Our Simple Philosophy</h2>
          <Row>
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Body className="p-4">
                  <h3 className="text-primary mb-3">You Be the Face</h3>
                  <ul className="list-unstyled">
                    <li className="mb-2">• Meet clients</li>
                    <li className="mb-2">• Show properties</li>
                    <li className="mb-2">• Negotiate deals</li>
                    <li className="mb-2">• Build relationships</li>
                    <li>• Close transactions</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Body className="p-4">
                  <h3 className="text-primary mb-3">We Run the Engine</h3>
                  <ul className="list-unstyled">
                    <li className="mb-2">• Respond to leads 24/7</li>
                    <li className="mb-2">• Qualify and nurture</li>
                    <li className="mb-2">• Handle compliance</li>
                    <li className="mb-2">• Manage technology</li>
                    <li>• Generate opportunities</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Benefits Grid */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-5">Everything You Need to Succeed</h2>
          <Row>
            {benefits.map((benefit, index) => (
              <Col lg={6} key={index} className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-start">
                      <div className="me-4">{benefit.icon}</div>
                      <div>
                        <h4>{benefit.title}</h4>
                        <p className="mb-0">{benefit.description}</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why Choose Us */}
      <section className="py-5 bg-light">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <h2 className="text-center mb-5">Why Agents Choose KairosIQ AI Realty</h2>
              <ul className="list-unstyled">
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-success me-3">✓</span>
                  <span>No franchise fees or royalties</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-success me-3">✓</span>
                  <span>Your own AI assistant included</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-success me-3">✓</span>
                  <span>Premium office with no monthly costs</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-success me-3">✓</span>
                  <span>Direct access to broker support</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-success me-3">✓</span>
                  <span>Technology that actually works</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-success me-3">✓</span>
                  <span>Join a growing team of successful agents</span>
                </li>
              </ul>
              <p className="text-center text-muted mt-4">
                <small>*Compensation details discussed during confidential consultation</small>
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form Section */}
      <section className="py-5" id="contact-form">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <Card className="shadow">
                <Card.Body className="p-5">
                  <h2 className="text-center mb-4">Request Your Confidential Information Package</h2>
                  <p className="text-center text-muted mb-5">
                    All fields are confidential and never shared.
                  </p>
                  
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone *</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Current Brokerage</Form.Label>
                          <Form.Control
                            type="text"
                            name="currentBrokerage"
                            value={formData.currentBrokerage}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Years in Real Estate</Form.Label>
                          <Form.Select
                            name="yearsExperience"
                            value={formData.yearsExperience}
                            onChange={handleInputChange}
                          >
                            <option value="">Select...</option>
                            <option value="0-1">Less than 1 year</option>
                            <option value="1-3">1-3 years</option>
                            <option value="3-5">3-5 years</option>
                            <option value="5-10">5-10 years</option>
                            <option value="10+">10+ years</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Annual Transactions</Form.Label>
                          <Form.Select
                            name="annualTransactions"
                            value={formData.annualTransactions}
                            onChange={handleInputChange}
                          >
                            <option value="">Select...</option>
                            <option value="0-5">0-5 deals</option>
                            <option value="6-10">6-10 deals</option>
                            <option value="11-20">11-20 deals</option>
                            <option value="21-50">21-50 deals</option>
                            <option value="50+">50+ deals</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Best Time to Call</Form.Label>
                          <Form.Select
                            name="bestTimeToCall"
                            value={formData.bestTimeToCall}
                            onChange={handleInputChange}
                          >
                            <option value="">Select...</option>
                            <option value="Morning">Morning (8am-12pm)</option>
                            <option value="Afternoon">Afternoon (12pm-5pm)</option>
                            <option value="Evening">Evening (5pm-8pm)</option>
                            <option value="Anytime">Anytime</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-4">
                      <Form.Label>What interests you most about KairosIQ AI Realty? (Optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <Form.Check
                        type="checkbox"
                        label="I'm interested in learning about opportunities at KairosIQ AI Realty"
                        required
                      />
                    </Form.Group>
                    
                    <div className="text-center">
                      <Button
                        type="submit"
                        size="lg"
                        className="px-5"
                        disabled={loading}
                      >
                        {loading ? 'Submitting...' : 'Submit - Request Information'}
                      </Button>
                    </div>
                    
                    <p className="text-center text-muted mt-4 mb-0">
                      <small>
                        Your information is confidential. We'll send you our agent package 
                        with technology overview and schedule a private consultation.
                      </small>
                    </p>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Contact Options */}
          <Row className="mt-5">
            <Col lg={12}>
              <h3 className="text-center mb-4">Three Ways to Connect</h3>
              <Row>
                <Col md={4} className="text-center mb-4">
                  <Card className="h-100 border-primary">
                    <Card.Body className="p-4">
                      <FaCalendar className="fs-1 text-primary mb-3" />
                      <h5>Office Tour</h5>
                      <p>Tour our Chandler Office & Technology</p>
                      <Button variant="outline-primary" href="#contact-form">Schedule Tour →</Button>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="text-center mb-4">
                  <Card className="h-100 border-primary">
                    <Card.Body className="p-4">
                      <FaPhone className="fs-1 text-primary mb-3" />
                      <h5>Call Direct</h5>
                      <p>Speak with Michael</p>
                      <Button variant="outline-primary" href="tel:602-860-6110">(602) 860-6110 →</Button>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="text-center mb-4">
                  <Card className="h-100 border-primary">
                    <Card.Body className="p-4">
                      <FaVideo className="fs-1 text-primary mb-3" />
                      <h5>Video Meeting</h5>
                      <p>At Your Convenience</p>
                      <Button variant="outline-primary" href="#contact-form">Book Meeting →</Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      <FooterWithPolicies />
    </>
  );
};

export default JoinBrokerage;